// Canvas2D helpers shared by the procedural world / FX / icon builders.
// Everything here draws into Phaser canvas textures at boot; nothing runs per frame.
// Art randomness is seeded (deterministic) so the game looks identical every boot.

import type Phaser from 'phaser';
import { css, mix } from '../../config/palette';
import { Rng, hashSeed } from '../../core/rng';

export type G = CanvasRenderingContext2D;

export interface CanvasTex {
  tex: Phaser.Textures.CanvasTexture;
  g: G;
  w: number;
  h: number;
}

/**
 * Creates a canvas texture for `key` unless that key already exists (an AI-generated
 * file from the manifest or an earlier build wins). Returns null when skipped.
 */
export function newCanvas(scene: Phaser.Scene, key: string, w: number, h: number): CanvasTex | null {
  if (scene.textures.exists(key)) return null;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return null;
  const g = tex.context;
  g.imageSmoothingEnabled = true;
  return { tex, g, w, h };
}

/** Registers numeric frames 0..count-1 laid out on a grid (row-major). */
export function addGridFrames(tex: Phaser.Textures.CanvasTexture, fw: number, fh: number, count: number, cols: number): void {
  for (let i = 0; i < count; i++) {
    tex.add(i, 0, (i % cols) * fw, Math.floor(i / cols) * fh, fw, fh);
  }
}

/** A detached scratch canvas (for compositing passes). */
export function scratch(w: number, h: number): { c: HTMLCanvasElement; g: G } {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d')!;
  return { c, g };
}

/** Deterministic RNG for one art asset. */
export function seeded(name: string): Rng {
  return new Rng(hashSeed('neon-art', name));
}

/** '#rrggbb' / 'rgba()' for a 0xRRGGBB colour. */
export const rgba = (color: number, alpha = 1): string => css(color, Math.max(0, Math.min(1, alpha)));

/** Mix towards white (t > 0) or black (t < 0). */
export function tone(color: number, t: number): number {
  return t >= 0 ? mix(color, 0xffffff, t) : mix(color, 0x000000, -t);
}

export const clamp = (v: number, a: number, b: number): number => (v < a ? a : v > b ? b : v);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const smooth = (t: number): number => t * t * (3 - 2 * t);
export const TAU = Math.PI * 2;

// ───────────────────────────── Paths ─────────────────────────────

export function roundRectPath(g: G, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  g.beginPath();
  g.moveTo(x + rr, y);
  g.lineTo(x + w - rr, y);
  g.arcTo(x + w, y, x + w, y + rr, rr);
  g.lineTo(x + w, y + h - rr);
  g.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  g.lineTo(x + rr, y + h);
  g.arcTo(x, y + h, x, y + h - rr, rr);
  g.lineTo(x, y + rr);
  g.arcTo(x, y, x + rr, y, rr);
  g.closePath();
}

/** Closed polygon from a flat [x0, y0, x1, y1, ...] list. */
export function polyPath(g: G, pts: readonly number[], close = true): void {
  g.beginPath();
  g.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i], pts[i + 1]);
  if (close) g.closePath();
}

/** Regular hexagon (pointy-top when rot = 0). */
export function hexPath(g: G, cx: number, cy: number, r: number, rot = 0): void {
  g.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = rot - Math.PI / 2 + (i * Math.PI) / 3;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
}

export function circlePath(g: G, x: number, y: number, r: number): void {
  g.beginPath();
  g.arc(x, y, Math.max(0.01, r), 0, TAU);
}

// ───────────────────────────── Fills / glows ─────────────────────────────

/** Radial soft dot; `core` is the fraction of the radius that stays at full alpha. */
export function softDot(g: G, x: number, y: number, r: number, color: number, alpha = 1, core = 0): void {
  if (r <= 0 || alpha <= 0) return;
  const grad = g.createRadialGradient(x, y, 0, x, y, r);
  grad.addColorStop(0, rgba(color, alpha));
  if (core > 0) grad.addColorStop(clamp(core, 0, 0.95), rgba(color, alpha));
  grad.addColorStop(clamp(core + (1 - core) * 0.35, 0, 0.97), rgba(color, alpha * 0.45));
  grad.addColorStop(clamp(core + (1 - core) * 0.7, 0, 0.98), rgba(color, alpha * 0.12));
  grad.addColorStop(1, rgba(color, 0));
  g.fillStyle = grad;
  g.fillRect(x - r, y - r, r * 2, r * 2);
}

/** Soft ellipse glow (scaled radial gradient). */
export function softEllipse(g: G, x: number, y: number, rx: number, ry: number, color: number, alpha = 1): void {
  if (rx <= 0 || ry <= 0) return;
  g.save();
  g.translate(x, y);
  g.scale(1, ry / rx);
  softDot(g, 0, 0, rx, color, alpha);
  g.restore();
}

/** Stroke the current path several times, wide+faint to thin+bright: a neon line. */
export function neonStroke(g: G, color: number, width: number, glow: number, alpha = 1, coreColor = 0xffffff, coreMix = 0.55): void {
  g.save();
  g.lineCap = 'round';
  g.lineJoin = 'round';
  const passes = [
    { w: width + glow * 2, a: 0.1 },
    { w: width + glow, a: 0.22 },
    { w: width + glow * 0.4, a: 0.5 },
  ];
  for (const p of passes) {
    g.strokeStyle = rgba(color, p.a * alpha);
    g.lineWidth = p.w;
    g.stroke();
  }
  g.strokeStyle = rgba(color, alpha);
  g.lineWidth = width;
  g.stroke();
  if (coreMix > 0) {
    g.strokeStyle = rgba(mix(color, coreColor, coreMix), alpha);
    g.lineWidth = Math.max(1, width * 0.45);
    g.stroke();
  }
  g.restore();
}

/** Fill with a vertical linear gradient defined by [offset, color, alpha] stops. */
export function vGradient(g: G, x: number, y: number, w: number, h: number, stops: readonly [number, number, number?][]): void {
  const grad = g.createLinearGradient(0, y, 0, y + h);
  for (const [o, c, a] of stops) grad.addColorStop(clamp(o, 0, 1), rgba(c, a ?? 1));
  g.fillStyle = grad;
  g.fillRect(x, y, w, h);
}

export function hGradient(g: G, x: number, y: number, w: number, h: number, stops: readonly [number, number, number?][]): void {
  const grad = g.createLinearGradient(x, 0, x + w, 0);
  for (const [o, c, a] of stops) grad.addColorStop(clamp(o, 0, 1), rgba(c, a ?? 1));
  g.fillStyle = grad;
  g.fillRect(x, y, w, h);
}

/** Pixel-exact filled rectangle (integer grid). */
export function px(g: G, x: number, y: number, w: number, h: number, color: number, alpha = 1): void {
  g.fillStyle = rgba(color, alpha);
  g.fillRect(x, y, w, h);
}

// ───────────────────────────── Noise ─────────────────────────────

/** 32-bit integer hash of a lattice point. */
export function hash2(i: number, j: number, seed: number): number {
  let h = (i * 374761393 + j * 668265263 + seed * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * Periodic value noise: lattice cell size `cell` px, repeating every periodX/periodY px
 * (must be multiples of `cell`), so textures built from it tile seamlessly.
 */
export class PeriodicNoise {
  private readonly cx: number;
  private readonly cy: number;
  constructor(
    private readonly seed: number,
    private readonly cell: number,
    periodX: number,
    periodY: number,
  ) {
    this.cx = Math.max(1, Math.round(periodX / cell));
    this.cy = Math.max(1, Math.round(periodY / cell));
  }

  at(x: number, y: number): number {
    const fx = x / this.cell;
    const fy = y / this.cell;
    const i0 = Math.floor(fx);
    const j0 = Math.floor(fy);
    const tx = smooth(fx - i0);
    const ty = smooth(fy - j0);
    const m = (v: number, n: number) => ((v % n) + n) % n;
    const a = hash2(m(i0, this.cx), m(j0, this.cy), this.seed);
    const b = hash2(m(i0 + 1, this.cx), m(j0, this.cy), this.seed);
    const c = hash2(m(i0, this.cx), m(j0 + 1, this.cy), this.seed);
    const d = hash2(m(i0 + 1, this.cx), m(j0 + 1, this.cy), this.seed);
    return lerp(lerp(a, b, tx), lerp(c, d, tx), ty);
  }
}

/** Fractal sum of periodic noise octaves (each octave halves the cell size). Returns ~0..1. */
export function fbm(octaves: PeriodicNoise[], x: number, y: number): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  for (const o of octaves) {
    sum += o.at(x, y) * amp;
    norm += amp;
    amp *= 0.5;
  }
  return sum / norm;
}

export function noiseOctaves(seed: number, cell: number, periodX: number, periodY: number, count: number): PeriodicNoise[] {
  const out: PeriodicNoise[] = [];
  let c = cell;
  for (let i = 0; i < count && c >= 1; i++) {
    out.push(new PeriodicNoise(seed + i * 7919, c, periodX, periodY));
    c /= 2;
  }
  return out;
}

/**
 * Per-pixel brightness modulation of an area already drawn on the canvas.
 * fn(x, y) returns a multiplier (1 = unchanged). Alpha is preserved.
 */
export function modulate(g: G, x0: number, y0: number, w: number, h: number, fn: (x: number, y: number) => number): void {
  const img = g.getImageData(x0, y0, w, h);
  const d = img.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (d[i + 3] === 0) continue;
      const k = fn(x, y);
      d[i] = clamp(d[i] * k, 0, 255);
      d[i + 1] = clamp(d[i + 1] * k, 0, 255);
      d[i + 2] = clamp(d[i + 2] * k, 0, 255);
    }
  }
  g.putImageData(img, x0, y0);
}

/**
 * Draw something that may straddle the left/right edge of a horizontally tiling
 * canvas of width W: it is drawn again shifted by ±W so the texture wraps seamlessly.
 */
export function wrapX(g: G, W: number, x0: number, x1: number, draw: () => void): void {
  draw();
  if (x0 < 0) {
    g.save();
    g.translate(W, 0);
    draw();
    g.restore();
  }
  if (x1 > W) {
    g.save();
    g.translate(-W, 0);
    draw();
    g.restore();
  }
}

/** Same as wrapX for both axes (small tileable textures). */
export function wrapXY(g: G, W: number, H: number, x0: number, y0: number, x1: number, y1: number, draw: () => void): void {
  const xs = [0];
  const ys = [0];
  if (x0 < 0) xs.push(W);
  if (x1 > W) xs.push(-W);
  if (y0 < 0) ys.push(H);
  if (y1 > H) ys.push(-H);
  for (const dx of xs) {
    for (const dy of ys) {
      g.save();
      g.translate(dx, dy);
      draw();
      g.restore();
    }
  }
}
